// RunTab.js
import React, { useState, useEffect, useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
    fetchBuildData,
    getDevicesPerPlatform,
    triggerTestExecution,
    clients,
    suites
} from './DataFlow';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { dateTemplate } from './DataFlow';
import { InputNumber } from 'primereact/inputnumber';
import { FilterService, FilterMatchMode } from 'primereact/api';

const RunTab = () => {
    const [selectedBuilds, setSelectedBuilds] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [branchPattern, setBranchPattern] = useState('');
    const [versionSearch, setVersionSearch] = useState('');
    const [selectedBuild, setSelectedBuild] = useState(null);
    const [selectedSuite, setSelectedSuite] = useState(null);
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [infrastructure, setInfrastructure] = useState([]);
    const [statusExecution, setExecutionStatus] = useState(null);
    const [powerUser, setPowerUser] = useState(false);

    const [buildRows, setBuildRows] = useState(5);
    const [buildFirst, setBuildFirst] = useState(0);
    const [hasFetched, setHasFetched] = useState(false);

    // Initialize filters with default values and match modes
    const [deviceFilters, setDeviceFilters] = useState({
        agentName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        model: { value: null, matchMode: FilterMatchMode.CONTAINS },
        platform: { value: null, matchMode: FilterMatchMode.CONTAINS },
        os: { value: null, matchMode: FilterMatchMode.CONTAINS },
        ram: { value: null, matchMode: 'custom' },
        enabled: { value: null, matchMode: FilterMatchMode.EQUALS },
        comments: { value: null, matchMode: FilterMatchMode.CONTAINS },
        type: { value: null, matchMode: FilterMatchMode.EQUALS },
    });

    const toast = useRef(null);

    useEffect(() => {
        if (!hasFetched) {
            fetchBuildData(
                selectedClient,
                branchPattern,
                versionSearch,
                setSelectedBuilds,
                setInfrastructure,
                setPowerUser
            );
            setHasFetched(true);
        }
    }, [selectedClient, branchPattern, versionSearch, powerUser, statusExecution, hasFetched]);

    FilterService.register('custom_ram', (value, filter) => {
        var from = null;
        var to = null;

        if (filter) {
            from = filter.from;
            to = filter.to;

        }
        if (from === null && to === null) return true;
        if (from !== null && to === null) return from <= value;
        if (from === null && to !== null) return value <= to;
        return from <= value && value <= to;
    });

    const onBuildsPageChange = (e) => {
        setBuildFirst(e.first);
        setBuildRows(e.rows);
    };

    const showNotificationRun = (toast, selectedSuite, selectedBuild) => {
        const cleanSelectedDevices = selectedDevices.filter((device) => device !== undefined);

        if (selectedSuite && selectedBuild && cleanSelectedDevices.length > 0) {
            toast.current.show({
                severity: 'success',
                summary: 'Scheduled',
                detail: 'Build executed!',
                life: 10000
            });
            triggerTestExecution(selectedSuite, selectedBuild, cleanSelectedDevices, setExecutionStatus);
        } else {
            let error = '';

            if (!selectedSuite) {
                error = 'Select suite!';
            } else if (!selectedBuild) {
                error = 'Select build!';
            } else if (cleanSelectedDevices.length === 0) {
                error = 'Select at least one device!';
            }

            toast.current.show({ severity: 'error', summary: 'Error', detail: error, life: 10000 });
        }
    };

    const onBuildSelectionChange = (e) => {
        setSelectedBuild(e.value);
        if (e.value != null) {
            setSelectedDevices([]); // Reset selected devices
            getDevicesPerPlatform(e.value, setInfrastructure);
        }
    };

    // Updated RangeFilter component
    const rangeFilterTemplate = (options) => {
        const from = options.value?.from || null;
        const to = options.value?.to || null;

        return (
            <div className="flex gap-1">
                <InputNumber
                    value={from}
                    onChange={(e) => {
                        options.filterApplyCallback({ from: e.value, to });
                    }}
                    placeholder="Min"
                />
                <InputNumber
                    value={to}
                    onChange={(e) => {
                        options.filterApplyCallback({ from, to: e.value });
                    }}
                    placeholder="Max"
                />
            </div>
        );
    };

    const enabledOptions = [
        { label: 'True', value: true },
        { label: 'False', value: false }
    ];

    const typeOptions = [
        { label: 'On-premise', value: 'On-premise' },
        { label: 'Cloud', value: 'Cloud' }
    ];

    return (
        <div>
            <div>
                <h2>Build Selection</h2>
                <Card>
                    <Toast ref={toast} />
                    <div
                        className="search-controls"
                        style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                    >
                        <Dropdown
                            value={selectedClient}
                            options={clients}
                            onChange={(e) => setSelectedClient(e.value)}
                            placeholder="Select Client"
                        />
                        <InputText
                            value={branchPattern}
                            onChange={(e) => setBranchPattern(e.target.value)}
                            placeholder="Branch Search Pattern"
                        />
                        <InputText
                            value={versionSearch}
                            onChange={(e) => setVersionSearch(e.target.value)}
                            placeholder="Version Search"
                        />
                        <Button
                            label="Filter"
                            icon="pi pi-filter"
                            onClick={() =>
                                fetchBuildData(selectedClient, branchPattern, versionSearch, setSelectedBuilds)
                            }
                            style={{ width: '100px' }}
                            className="p-button-sm"
                        />
                    </div>

                    <h3>Builds</h3>
                    <DataTable
                        value={selectedBuilds}
                        selection={selectedBuild}
                        first={buildFirst}
                        paginator
                        showGridlines
                        rows={buildRows}
                        onPage={onBuildsPageChange}
                        onSelectionChange={onBuildSelectionChange}
                        dataKey="_id"
                        rowsPerPageOptions={[5, 10, 20]}
                        className="dark-table"
                    >
                        <Column selectionMode="single" />
                        <Column field="gameName" header="Client" />
                        <Column field="pureVersion" header="PureVersion" />
                        <Column field="version" header="Version" />
                        <Column field="branch" header="Branch" />
                        <Column field="platform" header="Platform" />
                        <Column field="buildNo" header="Build Number" />
                        <Column field="creationDate" header="Created" sortable body={dateTemplate} />
                        <Column
                            header="Binary"
                            body={(rowData) => (
                                <a
                                    href={
                                        'https://console.cloud.google.com/storage/browser/' +
                                        rowData.googleBucketUrl
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Link
                                </a>
                            )}
                        />
                        <Column
                            header="Build"
                            body={(rowData) => (
                                <a href={rowData.buildUrl} target="_blank" rel="noopener noreferrer">
                                    Link
                                </a>
                            )}
                        />
                    </DataTable>
                </Card>

                <h2>Device Selection</h2>
                <Card
                    className={`${!powerUser ? 'disabled-card' : ''}`}
                    style={{ cursor: powerUser ? 'pointer' : 'not-allowed', opacity: powerUser ? 1 : 0.6 }}
                > 
                    <DataTable
                        value={infrastructure}
                        selection={selectedDevices}
                        onSelectionChange={(e) => setSelectedDevices(e.value)}
                        dataKey="id"
                        paginator
                        rows={10}
                        showGridlines
                        rowsPerPageOptions={[10, 20, 50]}
                        filters={deviceFilters}
                        onFilter={(e) => setDeviceFilters(e.filters)}
                        selectionMode="checkbox"
                        className={`${!powerUser ? 'disabled-card' : ''}`}
                        style={{ marginTop: '20px' }}
                    >
                        <Column selectionMode="multiple" headerStyle={{ width: '2em' }}></Column>
                        <Column
                            field="agentName"
                            header="Agent Name"
                            filter
                            filterPlaceholder="Search by Agent Name"
                            filterMatchMode="contains"
                        />
                        <Column
                            field="model"
                            header="Model"
                            filter
                            filterPlaceholder="Search by Model"
                            filterMatchMode="contains"
                        />
                        <Column
                            field="platform"
                            header="Platform"
                            filter
                            filterPlaceholder="Search by Platform"
                            filterMatchMode="contains"
                        />
                        <Column
                            field="os"
                            header="OS"
                            filter
                            filterPlaceholder="Search by OS"
                            filterMatchMode="contains"
                        />
                        <Column
                            field="ram"
                            header="RAM (GB)"
                            filter
                            sortable
                            filterElement={rangeFilterTemplate}
                            filterMatchMode="custom"
                            filterFunction={(value, filter) => {
                                const numValue = Number(value);
                                console.log("Value:", value, "numValue:", numValue, "filter:", filter);
                                const from = filter?.from || 0;
                                const to = filter?.to || Infinity;
                                console.log("from:", from, "to:", to);
                                const result = numValue >= from && numValue <= to;
                                console.log("Result:", result);
                                return result;
                            }}
                        />
                        <Column
                            field="enabled"
                            header="Enabled"
                            filter
                            filterElement={
                                <Dropdown
                                    options={enabledOptions}
                                    value={deviceFilters.enabled?.value}
                                    onChange={(e) =>
                                        setDeviceFilters((prevFilters) => ({
                                            ...prevFilters,
                                            enabled: {
                                                value: e.value,
                                                matchMode: FilterMatchMode.EQUALS
                                            }
                                        }))
                                    }
                                    placeholder="Select"
                                    className="p-column-filter"
                                />
                            }
                        />
                        <Column
                            field="type"
                            header="Type"
                            filter
                            filterElement={
                                <Dropdown
                                    options={typeOptions}
                                    value={deviceFilters.type?.value}
                                    onChange={(e) =>
                                        setDeviceFilters((prevFilters) => ({
                                            ...prevFilters,
                                            type: {
                                                value: e.value,
                                                matchMode: FilterMatchMode.EQUALS
                                            }
                                        }))
                                    }
                                    placeholder="Select Type"
                                    className="p-column-filter"
                                />
                            }
                        />
                        <Column
                            sortable
                            field="screenWidth"
                            header="Screen Width (pixels)"
                        />
                        <Column
                            sortable
                            field="screenHeight"
                            header="Screen Height (pixels)"
                        />
                        <Column
                            field="comments"
                            header="Comments"
                            filter
                            filterPlaceholder="Search by Comments"
                            filterMatchMode="contains"
                        />
                    </DataTable>
                </Card>

                <h2>Select Test Suite</h2>
                <Card>
                 <div
                        className="search-controls"
                        style={{ display: 'flex', alignItems: 'center', margin: '10px', gap: '10px' }}
                    >
                        <Dropdown
                            disabled={!powerUser}
                            value={selectedSuite}
                            onChange={(e) => setSelectedSuite(e.value)}
                            options={suites}
                            optionLabel="name"
                            placeholder="Select suite"
                        />
                        <Button
                            label="Run"
                            icon="pi pi-fw pi-play"
                            onClick={() => showNotificationRun(toast, selectedSuite, selectedBuild)}
                            disabled={!powerUser}
                            style={{ width: '100px' }}
                            className="p-button-sm"
                        />
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default RunTab;
